import { useEffect, useState } from "react";
import { Info, Copy, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * AboutPanel — V1.1.1 OP-006 · Administration → About tab.
 *
 * Reuses the existing /api/admin/diagnostics/system endpoint so we
 * inherit build metadata, environment, and connectivity summary without
 * a second source of truth. Purpose is user-facing rather than
 * diagnostic — friendly presentation of what version we're on and when
 * we last deployed.
 */
export default function AboutPanel() {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/admin/diagnostics/system");
        if (alive) setInfo(r.data);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Could not load version info");
      }
    })();
    return () => { alive = false; };
  }, []);

  const copyDiagString = async () => {
    if (!info) return;
    const s = [
      `Application  : ${info.app.name}`,
      `Version      : ${info.app.version}`,
      `Build        : ${info.app.build}`,
      `Git Commit   : ${info.app.git_commit || "—"}`,
      `Environment  : ${info.app.environment}`,
      `Deployed     : ${info.app.started_at_utc}`,
      `Python       : ${info.app.python_version}`,
    ].join("\n");
    try {
      await navigator.clipboard.writeText(s);
      toast.success("Version details copied");
    } catch {
      toast.error("Could not access clipboard");
    }
  };

  return (
    <div data-testid="admin-panel-about">
      <div className="mb-5 border-b border-slate-200 pb-3">
        <h2 className="font-display text-lg font-semibold text-slate-900">About</h2>
        <p className="mt-0.5 text-sm text-slate-500">
          Application version and build details. Include this in every
          support request so we know exactly what you&apos;re running.
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          {error}
        </div>
      )}

      {!info && !error && (
        <div className="py-6 text-center text-sm text-slate-500">Loading…</div>
      )}

      {info && (
        <div className="space-y-3">
          <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-charcoal text-ink-inverse">
                <Info size={18} weight="bold" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900">{info.app.name}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">
                  A Coolman Fuels internal platform
                </div>
              </div>
              <button
                data-testid="about-copy"
                onClick={copyDiagString}
                className="ml-auto inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
              >
                <Copy size={12} weight="bold" /> Copy
              </button>
            </div>
          </div>

          <dl className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <Row label="Application Version" value={info.app.version} testid="about-version" mono />
            <Row label="Build Number"       value={info.app.build}   testid="about-build"   mono />
            <Row label="Git Commit"         value={info.app.git_commit || "—"} testid="about-commit" mono />
            <Row label="Environment"        value={info.app.environment} testid="about-environment">
              <EnvBadge env={info.app.environment} />
            </Row>
            <Row label="Deployment Date"    value={formatIso(info.app.started_at_utc)} testid="about-deployed" />
            <Row label="Server Time"        value={formatIso(info.app.server_time_utc)} testid="about-server-time" muted />
          </dl>

          <p className="text-[11px] text-slate-400">
            Deployment Date reflects the server&apos;s current uptime start.
            For a full connectivity check use{" "}
            <span className="font-medium text-slate-600">System Diagnostics</span>.
          </p>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, testid, mono, muted, children }) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-md border border-slate-200 bg-white p-3">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div
        data-testid={testid}
        className={`text-sm ${mono ? "font-mono" : ""} ${muted ? "text-slate-500" : "text-slate-900"} text-right`}
      >
        {children || value || "—"}
      </div>
    </div>
  );
}

function EnvBadge({ env }) {
  const tone =
    env === "Production" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
    env === "Preview"    ? "bg-sky-50 text-sky-700 border-sky-200" :
                           "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${tone}`}>
      <CheckCircle size={11} weight="fill" /> {env}
    </span>
  );
}

function formatIso(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-CA", { hour12: false });
}
